import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from './apiClient';
import { useAuthStore } from './authStore';

export type ReminderSource = 'voice' | 'chat' | 'extension' | 'manual';
export type ReminderStatus = 'pending' | 'done' | 'dismissed';

export interface ScrudixReminder {
  id: string;
  title: string;
  detail: string;
  source: ReminderSource;
  status: ReminderStatus;
  createdAt: string;
  updatedAt: string;
  remindAt?: string;
  snoozeUntil?: string;
  recurrence?: string;
  lastNotifiedAt?: string;
  originUrl?: string;
}

interface ReminderState {
  reminders: ScrudixReminder[];
  dismissedAt?: string;
  isLoading: boolean;
  hydrated: boolean;
  lastSyncedAt?: string;
  pendingCreations: number;
  setReminders: (reminders: ScrudixReminder[]) => void;
  addReminder: (reminder: ScrudixReminder) => void;
  createReminder: (input: {
    title: string;
    detail?: string;
    source?: ReminderSource;
    remindAt?: string;
    originUrl?: string;
  }) => Promise<ScrudixReminder | null>;
  updateReminder: (id: string, patch: Partial<Omit<ScrudixReminder, 'id' | 'createdAt'>>) => Promise<void>;
  snoozeReminder: (id: string, until: string) => Promise<void>;
  dismissReminder: (id: string) => Promise<void>;
  deleteReminder: (id: string) => Promise<void>;
  dismissAll: () => Promise<void>;
  syncReminders: () => Promise<void>;
  isPendingAndActive: (reminder: ScrudixReminder) => boolean;
  pendingCount: () => number;
  hasPending: () => boolean;
}

const STORAGE_KEY = 'scrudix:reminders:v1';

function makeLocalId(): string {
  return `local-reminder-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const useReminderStore = create<ReminderState>()(
  persist(
    (set, get) => ({
      reminders: [],
      dismissedAt: undefined,
      isLoading: false,
      hydrated: false,
      lastSyncedAt: undefined,
      pendingCreations: 0,

      setReminders: (reminders) => set({ reminders }),

      addReminder: (reminder) =>
        set((state) => ({
          reminders: [reminder, ...state.reminders].slice(0, 100),
        })),

      createReminder: async (input) => {
        const source = input.source ?? 'manual';
        const title = input.title.trim();
        const detail = (input.detail ?? '').trim();
        if (!title && !detail) return null;

        const localReminder: ScrudixReminder = {
          id: makeLocalId(),
          title: title || detail.slice(0, 80),
          detail: detail || title,
          source,
          status: 'pending',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          remindAt: input.remindAt,
          originUrl: input.originUrl,
        };

        set((state) => ({
          reminders: [localReminder, ...state.reminders].slice(0, 100),
          pendingCreations: state.pendingCreations + 1,
        }));

        try {
          const response = await apiClient.createReminder({
            title: localReminder.title,
            detail: localReminder.detail,
            source,
            remindAt: input.remindAt,
            originUrl: input.originUrl,
          });
          if (response.success && response.data) {
            set((state) => ({
              reminders: state.reminders.map((r) => (r.id === localReminder.id ? response.data : r)),
              pendingCreations: Math.max(0, state.pendingCreations - 1),
              lastSyncedAt: new Date().toISOString(),
            }));
            return response.data;
          }
        } catch {
          // Keep local reminder; will sync later.
        }
        set((state) => ({ pendingCreations: Math.max(0, state.pendingCreations - 1) }));
        return localReminder;
      },

      updateReminder: async (id, patch) => {
        set((state) => ({
          reminders: state.reminders.map((r) =>
            r.id === id ? { ...r, ...patch, updatedAt: new Date().toISOString() } : r
          ),
        }));
        try {
          await apiClient.updateReminder(id, patch);
        } catch {
          // ignore
        }
      },

      snoozeReminder: async (id, until) => {
        set((state) => ({
          reminders: state.reminders.map((r) =>
            r.id === id ? { ...r, snoozeUntil: until, updatedAt: new Date().toISOString() } : r
          ),
        }));
        try {
          await apiClient.snoozeReminder(id, until);
        } catch {
          // ignore
        }
      },

      dismissReminder: async (id) => {
        set((state) => ({
          reminders: state.reminders.map((r) =>
            r.id === id ? { ...r, status: 'dismissed' as const, snoozeUntil: undefined, updatedAt: new Date().toISOString() } : r
          ),
        }));
        try {
          await apiClient.dismissReminder(id);
        } catch {
          // ignore
        }
      },

      deleteReminder: async (id) => {
        set((state) => ({ reminders: state.reminders.filter((r) => r.id !== id) }));
        try {
          await apiClient.deleteReminder(id);
        } catch {
          // ignore
        }
      },

      dismissAll: async () => {
        set((state) => ({
          reminders: state.reminders.map((r) =>
            r.status === 'pending' ? { ...r, status: 'dismissed' as const, updatedAt: new Date().toISOString() } : r
          ),
          dismissedAt: new Date().toISOString(),
        }));
        try {
          await apiClient.dismissAllReminders();
        } catch {
          // ignore
        }
      },

      syncReminders: async () => {
        const authState = useAuthStore.getState();
        if (!authState.isAuthenticated || !authState.token) {
          set({ isLoading: false, hydrated: true });
          return;
        }

        set({ isLoading: true });
        try {
          const response = await apiClient.listReminders();
          if (response.success && response.data) {
            const serverReminders = response.data.reminders;
            set((state) => {
              const localOnlyPending = state.reminders.filter(
                (r) => r.id.startsWith('local-') && r.status === 'pending'
              );
              const merged = [...localOnlyPending, ...serverReminders];
              const byId = new Map<string, ScrudixReminder>();
              for (const r of merged) {
                const existing = byId.get(r.id);
                if (!existing || new Date(r.updatedAt) > new Date(existing.updatedAt)) {
                  byId.set(r.id, r);
                }
              }
              return {
                reminders: Array.from(byId.values())
                  .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
                  .slice(0, 100),
                dismissedAt: response.data.dismissedAt,
                lastSyncedAt: new Date().toISOString(),
                isLoading: false,
                hydrated: true,
              };
            });
          }
        } catch {
          set({ isLoading: false, hydrated: true });
        }
      },

      isPendingAndActive: (r: ScrudixReminder) => {
        if (r.status !== 'pending') return false;
        if (!r.snoozeUntil) return true;
        return new Date(r.snoozeUntil) <= new Date();
      },

      pendingCount: () => get().reminders.filter((r) => get().isPendingAndActive(r)).length,
      hasPending: () => get().reminders.some((r) => get().isPendingAndActive(r)),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        reminders: state.reminders,
        dismissedAt: state.dismissedAt,
        lastSyncedAt: state.lastSyncedAt,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) state.hydrated = true;
      },
    }
  )
);
